import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaRunning, FaBicycle, FaTimes, FaChevronLeft, FaSearch, FaSortAmountDown, FaSpinner } from 'react-icons/fa';
import './ImportSourceModal.css';

const SORT_OPTIONS = [
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
  { value: 'title-asc', label: 'Title A–Z' },
  { value: 'title-desc', label: 'Title Z–A' },
];

function dedupeRidesByRideId(rides) {
  const seen = new Set();
  return rides.filter((r) => r?.rideId && !seen.has(r.rideId) && (seen.add(r.rideId), true));
}

const ImportSourceModal = ({
  isOpen,
  onClose,
  onImport,
  stravaConnected,
  brakeaceConnected,
  defaultStep,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('source'); // 'source' | 'strava-list' | 'brakeace-list'
  const [stravaActivities, setStravaActivities] = useState([]);
  const [brakeaceRides, setBrakeaceRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    if (!isOpen) {
      setStep('source');
      setStravaActivities([]);
      setBrakeaceRides([]);
      setCursor(null);
      setError(null);
      setSearchTerm('');
      setSortBy('date-desc');
    } else if (defaultStep === 'brakeace-list') {
      setStep('brakeace-list');
      if (brakeaceConnected) {
        setLoading(true);
        setError(null);
        setCursor(null);
        const fn = httpsCallable(getFunctions(), 'brakeaceListRides');
        fn({ limit: 50 })
          .then((res) => {
            const data = res?.data || {};
            const rides = dedupeRidesByRideId(data.rides || []);
            setBrakeaceRides(rides);
            setCursor(data.nextCursor ?? null);
          })
          .catch((err) => setError(err?.message || 'Failed to load BrakeAce rides'))
          .finally(() => setLoading(false));
      }
    } else if (defaultStep === 'strava-list' && stravaConnected) {
      setStep('strava-list');
      setLoading(true);
      setError(null);
      const fn = httpsCallable(getFunctions(), 'stravaListActivities');
      fn({ rangeDays: 90, limit: 50 })
        .then((res) => setStravaActivities(res?.data?.activities || []))
        .catch((err) => setError(err?.message || 'Failed to load Strava activities'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, defaultStep, stravaConnected, brakeaceConnected]);

  const handleChooseStrava = async () => {
    if (!stravaConnected) {
      navigate('/profile');
      onClose();
      return;
    }
    setStep('strava-list');
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'stravaListActivities');
      const res = await fn({ rangeDays: 90, limit: 50 });
      setStravaActivities(res?.data?.activities || []);
    } catch (err) {
      setError(err?.message || 'Failed to load Strava activities');
    } finally {
      setLoading(false);
    }
  };

  const handleChooseBrakeace = async () => {
    if (!brakeaceConnected) {
      navigate('/profile');
      onClose();
      return;
    }
    setStep('brakeace-list');
    setLoading(true);
    setError(null);
    setCursor(null);
    try {
      const fn = httpsCallable(getFunctions(), 'brakeaceListRides');
      const res = await fn({ limit: 50 });
      const data = res?.data || {};
      const rides = dedupeRidesByRideId(data.rides || []);
      setBrakeaceRides(rides);
      setCursor(data.nextCursor ?? null);
    } catch (err) {
      setError(err?.message || 'Failed to load BrakeAce rides');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreBrakeace = async () => {
    if (!cursor) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'brakeaceListRides');
      const res = await fn({ limit: 50, cursor });
      const data = res?.data || {};
      const newRides = dedupeRidesByRideId(data.rides || []);
      setBrakeaceRides((prev) => {
        const existingIds = new Set(prev.map((r) => r.rideId));
        const toAdd = newRides.filter((r) => r?.rideId && !existingIds.has(r.rideId));
        return [...prev, ...toAdd];
      });
      setCursor(data.nextCursor ?? null);
    } catch (err) {
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStrava = async (activity) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'stravaImportActivity');
      const res = await fn({ activityId: activity.id });
      onImport({
        runName: res?.data?.runName || activity.name || `Strava ${activity.id}`,
        telemetryData: res?.data?.telemetryData || [],
        startDate: res?.data?.startDate || activity.start_date || null,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to import activity');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBrakeace = async (ride) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'brakeaceExportRide');
      const res = await fn({ rideId: ride.rideId });
      onImport({
        runName: res?.data?.title || ride.title || `BrakeAce ${ride.rideId}`,
        telemetryData: res?.data?.telemetryData || [],
      });
      onClose();
    } catch (err) {
      const msg = err?.message || (typeof err?.details === 'string' ? err.details : err?.details?.message) || 'Failed to import ride';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (val) => {
    if (val == null) return '—';
    try {
      let d;
      if (typeof val === 'number' && Number.isFinite(val)) {
        d = val > 1e12 ? new Date(val) : new Date(val * 1000);
      } else if (typeof val === 'string') {
        d = new Date(val);
      } else {
        d = val;
      }
      if (!d || Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  const getBrakeaceDate = (ride) => {
    const val = ride?.start ?? ride?.date ?? parseRideIdAsDate(ride?.rideId);
    if (!val) return '—';
    const d = typeof val === 'number' ? new Date(val > 1e12 ? val : val * 1000) : new Date(val);
    return Number.isNaN(d.getTime()) ? '—' : formatDate(d);
  };

  const getBrakeaceDateValue = (ride) => {
    const val = ride?.start ?? ride?.date ?? parseRideIdAsDate(ride?.rideId);
    if (!val) return null;
    const d = typeof val === 'number' ? new Date(val > 1e12 ? val : val * 1000) : new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const parseRideIdAsDate = (rideId) => {
    if (!rideId || typeof rideId !== 'string') return null;
    const m = rideId.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    return `${y}-${mo}-${d}T${h}:${mi}:${s}Z`;
  };

  const getSortKey = (item, source) => {
    if (source === 'strava') {
      const dateVal = item.start_date ? new Date(item.start_date).getTime() : 0;
      return { date: dateVal, title: (item.name || '').toLowerCase() };
    }
    const d = getBrakeaceDateValue(item);
    return { date: d ? d.getTime() : 0, title: (item.title || item.name || item.rideId || '').toLowerCase() };
  };

  const filteredAndSortedStrava = useMemo(() => {
    let list = stravaActivities;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((a) => (a.name || '').toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      const ka = getSortKey(a, 'strava');
      const kb = getSortKey(b, 'strava');
      if (sortBy === 'date-desc') return kb.date - ka.date;
      if (sortBy === 'date-asc') return ka.date - kb.date;
      if (sortBy === 'title-asc') return ka.title.localeCompare(kb.title);
      if (sortBy === 'title-desc') return kb.title.localeCompare(ka.title);
      return 0;
    });
    return sorted;
  }, [stravaActivities, searchTerm, sortBy]);

  const filteredAndSortedBrakeace = useMemo(() => {
    let list = brakeaceRides;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((r) => (r.title || r.name || r.rideId || '').toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      const ka = getSortKey(a, 'brakeace');
      const kb = getSortKey(b, 'brakeace');
      if (sortBy === 'date-desc') return kb.date - ka.date || ka.title.localeCompare(kb.title);
      if (sortBy === 'date-asc') return ka.date - kb.date || ka.title.localeCompare(kb.title);
      if (sortBy === 'title-asc') return ka.title.localeCompare(kb.title);
      if (sortBy === 'title-desc') return kb.title.localeCompare(ka.title);
      return 0;
    });
    return sorted;
  }, [brakeaceRides, searchTerm, sortBy]);

  if (!isOpen) return null;

  return (
    <div className="import-source-modal-overlay" onClick={onClose}>
      <div className="import-source-modal" onClick={(e) => e.stopPropagation()}>
        <div className="import-source-modal-header">
          <h2>{step === 'source' ? 'Import Ride' : step === 'strava-list' ? 'Import from Strava' : 'Import from BrakeAce'}</h2>
          <button type="button" className="import-source-modal-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {step === 'source' && (
          <div className="import-source-choices">
            <button
              type="button"
              className="import-source-choice"
              onClick={handleChooseStrava}
              disabled={!stravaConnected}
            >
              <FaRunning className="import-source-icon" style={{ color: '#FC4C02' }} />
              <span>Import from Strava</span>
              {!stravaConnected && (
                <span className="import-source-hint">Connect in Profile first</span>
              )}
            </button>
            <button
              type="button"
              className="import-source-choice"
              onClick={handleChooseBrakeace}
              disabled={!brakeaceConnected}
            >
              <FaBicycle className="import-source-icon" style={{ color: '#00d4aa' }} />
              <span>Import from BrakeAce</span>
              {!brakeaceConnected && (
                <span className="import-source-hint">Connect in Profile first</span>
              )}
            </button>
          </div>
        )}

        {(step === 'strava-list' || step === 'brakeace-list') && (
          <>
            <button
              type="button"
              className="import-source-back"
              onClick={() => setStep('source')}
            >
              <FaChevronLeft /> Back
            </button>
            {error && (
              <div className="import-source-error">{error}</div>
            )}
            {loading && step === 'strava-list' && stravaActivities.length === 0 && (
              <div className="import-source-loading">
                <FaSpinner className="import-source-spinner" aria-hidden />
                <span>Loading activities...</span>
              </div>
            )}
            {loading && step === 'brakeace-list' && brakeaceRides.length === 0 && (
              <div className="import-source-loading">
                <FaSpinner className="import-source-spinner" aria-hidden />
                <span>Loading rides...</span>
              </div>
            )}
            {(step === 'strava-list' || step === 'brakeace-list') && (stravaActivities.length > 0 || brakeaceRides.length > 0) && (
              <div className="import-source-list-controls">
                <div className="import-source-search">
                  <FaSearch className="import-source-search-icon" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="import-source-search-input"
                  />
                </div>
                <div className="import-source-sort">
                  <FaSortAmountDown className="import-source-sort-icon" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="import-source-sort-select"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {step === 'strava-list' && stravaActivities.length > 0 && (
              <div className="import-source-list">
                {filteredAndSortedStrava.length === 0 && searchTerm ? (
                  <p className="import-source-empty-filter">No activities match &quot;{searchTerm}&quot;</p>
                ) : (
                  filteredAndSortedStrava.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="import-source-item"
                      onClick={() => handleSelectStrava(a)}
                      disabled={loading}
                    >
                      <span className="import-source-item-name">{a.name || `Activity ${a.id}`}</span>
                      <span className="import-source-item-meta">
                        {formatDate(a.start_date)}
                        {a.moving_time != null && ` · ${Math.round(a.moving_time / 60)} min`}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
            {step === 'brakeace-list' && brakeaceRides.length > 0 && (
              <div className="import-source-list">
                {filteredAndSortedBrakeace.length === 0 && searchTerm ? (
                  <p className="import-source-empty-filter">No rides match &quot;{searchTerm}&quot;</p>
                ) : (
                  filteredAndSortedBrakeace.map((r) => (
                    <button
                      key={r.rideId}
                      type="button"
                      className="import-source-item"
                      onClick={() => handleSelectBrakeace(r)}
                      disabled={loading}
                    >
                      <span className="import-source-item-name">{r.title || r.name || r.rideId}</span>
                      <span className="import-source-item-meta">{getBrakeaceDate(r)}</span>
                    </button>
                  ))
                )}
                {cursor && (
                  <button
                    type="button"
                    className="import-source-load-more"
                    onClick={handleLoadMoreBrakeace}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="import-source-spinner import-source-spinner-sm" aria-hidden />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ImportSourceModal;
